/*
  <cart-drawer>: slide-in cart. Opened by the header cart icon and by
  <product-form> after adding. Quantity changes go through /cart/change.js
  and re-render the drawer via the Section Rendering API.
*/
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.overlay = this.querySelector('.drawer-overlay');
      this.sectionIds = [this.dataset.sectionId];

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) {
          this.close();
          return;
        }
        const changeButton = event.target.closest('[data-quantity-change]');
        if (changeButton) {
          const line = Number(changeButton.dataset.line || changeButton.closest('[data-line]')?.dataset.line);
          this.changeQuantity(line, Number(changeButton.dataset.quantityChange));
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-cart-note]')) this.updateNote(event.target.value);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-cart-drawer-toggle]');
        if (!toggle) return;
        event.preventDefault();
        this.open(toggle);
      });
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.releaseFocus = window.trapFocus ? window.trapFocus(this.panel) : null;
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      this.releaseFocus?.();
      this.trigger?.focus?.();
    }

    renderSections(sections, { justAdded = false } = {}) {
      const html = sections?.[this.dataset.sectionId];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer-Inner');
      const current = this.querySelector('#CartDrawer-Inner');
      if (fresh && current) current.replaceWith(fresh);
      if (justAdded) this.querySelector('[data-cart-notice]')?.removeAttribute('hidden');
      if (this.classList.contains('is-open')) {
        this.releaseFocus?.();
        this.releaseFocus = window.trapFocus ? window.trapFocus(this.panel) : null;
      }
    }

    async refreshCount() {
      try {
        const cart = await (await fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (error) {
        console.error(error);
      }
    }

    async changeQuantity(line, quantity) {
      if (!line || quantity < 0) return;
      this.classList.add('is-busy');
      try {
        const response = await fetch(`${window.theme.routes.cart_change}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds, sections_url: window.location.pathname }),
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message);
        this.renderSections(cart.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (error) {
        console.error(error);
      } finally {
        this.classList.remove('is-busy');
      }
    }

    async updateNote(note) {
      try {
        await fetch(`${window.theme.routes.cart_update}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (error) {
        console.error(error);
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}
